import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, Mail, Phone, Building2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (adminKey) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setRows(res.data);
      setAuthed(true);
    } catch (e) {
      toast.error("Invalid admin key.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!key) return;
    load(key);
  };

  return (
    <div className="min-h-screen bg-obsidian text-[#f2f4f0]">
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 py-10">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#8d948f] hover:text-brand transition-colors" data-testid="admin-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <span className="font-display font-extrabold uppercase tracking-tight text-brand">
            Delta Grassmasters · Admin
          </span>
        </div>

        {!authed ? (
          <form onSubmit={submit} className="max-w-md mx-auto mt-24 border border-white/10 bg-surface p-8" data-testid="admin-login-form">
            <Lock className="h-8 w-8 text-brand mb-6" />
            <h1 className="font-display font-bold uppercase tracking-tight text-2xl mb-2">
              Submissions Viewer
            </h1>
            <p className="text-sm text-[#8d948f] mb-6">
              Enter the admin key to view contact form submissions.
            </p>
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Admin key"
              className="w-full bg-transparent border-b border-white/20 py-3 focus:border-brand focus:outline-none transition-colors"
              data-testid="admin-key-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full bg-brand py-3 font-bold text-[#0b120a] hover:bg-brand-hover transition-colors disabled:opacity-60"
              data-testid="admin-login-btn"
            >
              {loading ? "Verifying..." : "Unlock"}
            </button>
          </form>
        ) : (
          <div data-testid="admin-dashboard">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-display font-bold uppercase tracking-tight text-3xl">
                {rows.length} Submission{rows.length !== 1 ? "s" : ""}
              </h1>
              <button
                onClick={() => load(key)}
                className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 text-sm hover:border-brand hover:text-brand transition-colors"
                data-testid="admin-refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>

            {rows.length === 0 ? (
              <p className="text-[#8d948f] mt-16 text-center">No submissions yet.</p>
            ) : (
              <div className="space-y-4">
                {rows.map((r) => (
                  <div key={r.id} className="border border-white/10 bg-surface p-6" data-testid="submission-row">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div>
                        <p className="font-semibold text-lg">{r.name}</p>
                        {r.company && (
                          <p className="text-sm text-[#8d948f] flex items-center gap-2">
                            <Building2 className="h-3.5 w-3.5" /> {r.company}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-[#5c625b]">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#c7ccc4] mb-4">
                      <a href={`mailto:${r.email}`} className="flex items-center gap-2 hover:text-brand">
                        <Mail className="h-4 w-4 text-brand" /> {r.email}
                      </a>
                      <a href={`tel:${r.phone}`} className="flex items-center gap-2 hover:text-brand">
                        <Phone className="h-4 w-4 text-brand" /> {r.phone}
                      </a>
                      <span className="text-[#8d948f]">
                        Prefers: <span className="text-brand">{r.preferred_contact}</span>
                      </span>
                    </div>
                    <p className="text-[15px] text-[#e2e6df] leading-relaxed border-l-2 border-brand pl-4">
                      {r.message}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
